import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Payslip } from '@/types';
import { useToast } from '@/hooks/use-toast';
import { useAppDataContext } from '@/contexts/AppDataContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { PayslipService } from '@/services/payslipService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/lib/utils';
import { 
  Search, 
  Filter, 
  Download, 
  Printer, 
  Mail, 
  RefreshCw, 
  Plus,
  Eye,
  Calendar,
  User,
  DollarSign,
  Archive
} from 'lucide-react';
import { EmailPayslipDialog } from '@/components/payslips/EmailPayslipDialog';
import { PayslipDocument } from '@/components/payslips/PayslipDocument';
import { getApiErrorMessage } from '@/services/api';

const PAYSLIP_EXPORT_STYLES = `
  #payslip-document, #payslip-document * {
    overflow: visible !important;
    text-overflow: clip !important;
  }
  #payslip-document .truncate,
  #payslip-document .line-clamp-1 {
    overflow: visible !important;
    white-space: normal !important;
    text-overflow: clip !important;
  }
  .bg-white { background: white !important; }
  .text-gray-900 { color: #111827 !important; }
  .text-gray-600 { color: #4B5563 !important; }
  .text-gray-500 { color: #6B7280 !important; }
  .bg-blue-600 { background: #2563EB !important; }
  .bg-green-50 { background: #F0FDF4 !important; }
  .bg-red-50 { background: #FEF2F2 !important; }
  .bg-blue-50 { background: #EFF6FF !important; }
  .bg-gray-50 { background: #F9FAFB !important; }
  .text-green-800 { color: #166534 !important; }
  .text-red-800 { color: #991B1B !important; }
  .text-blue-900 { color: #1E3A8A !important; }
  .text-blue-700 { color: #1D4ED8 !important; }
  .text-red-600 { color: #DC2626 !important; }
  .text-green-600 { color: #16A34A !important; }
  .border-gray-200 { border-color: #E5E7EB !important; }
  .border-gray-400 { border-color: #9CA3AF !important; }
  .rounded-lg { border-radius: 0.5rem !important; }
  .rounded { border-radius: 0.25rem !important; }
`;

const Payslips = () => {
  const [selectedPayslip, setSelectedPayslip] = useState<Payslip | null>(null);
  const { data: contextData, refresh } = useAppDataContext();
  const { user, council } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { employees, departments, positions } = contextData;
  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [filteredPayslips, setFilteredPayslips] = useState<Payslip[]>([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);

  // Simplified filter state
  const [filters, setFilters] = useState({
    search: '',
    year: new Date().getFullYear().toString(),
    month: (new Date().getMonth() + 1).toString().padStart(2, '0'),
    status: user?.role === 'employee' ? 'approved' : 'all',
  });

  // Current period for API calls
  const currentPeriod = `${filters.year}-${filters.month}`;

  // Get current user's employee record id (portal users linked via userId or email)
  const getCurrentUserEmployeeId = () => {
    if (!user) return null;

    const byUserId = employees.find(
      (emp) => emp.userId != null && String(emp.userId) === String(user.id)
    );
    if (byUserId) return byUserId.id;

    if (user.email) {
      const normalizedEmail = user.email.toLowerCase();
      const byEmail = employees.find(
        (emp) => emp.email?.toLowerCase() === normalizedEmail
      );
      if (byEmail) return byEmail.id;
    }

    return null;
  };

  // Check if user is an employee (should only see their own payslips)
  const isEmployee = user?.role === 'employee';
  const currentUserEmployeeId = getCurrentUserEmployeeId();

  useEffect(() => {
    if (isEmployee) {
      refresh.employees();
    }
  }, [isEmployee]);

  // Load payslips for current period when component mounts or period changes
  useEffect(() => {
    loadPayslipsForPeriod(currentPeriod);
  }, [currentPeriod]);

  // Filter payslips based on search and status filters
  useEffect(() => {
    let result = [...payslips];
    
    // Apply search filter
    if (filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      result = result.filter(payslip => {
        const employee = employees.find(emp => emp.id === payslip.employeeId);
        const employeeName = employee ? `${employee.firstName} ${employee.lastName}`.toLowerCase() : '';
        
        return employeeName.includes(searchTerm) || 
               payslip.employeeIdNumber?.toLowerCase().includes(searchTerm) ||
               payslip.employeeName?.toLowerCase().includes(searchTerm);
      });
    }
    
    // Apply status filter
    if (filters.status && filters.status !== 'all') {
      result = result.filter(payslip => {
        const status = (payslip.status || '').trim().toLowerCase();
        return status === filters.status;
      });
    }
    
    setFilteredPayslips(result);
    
    // Auto-select first payslip if none selected
    if (result.length > 0 && !selectedPayslip) {
      setSelectedPayslip(result[0]);
    }
  }, [payslips, filters.search, filters.status, employees, selectedPayslip]);

  const loadPayslipsForPeriod = async (period: string) => {
    try {
      setRefreshing(true);

      
      let payslipsData: Payslip[] = [];
      
      if (isEmployee && currentUserEmployeeId) {
        payslipsData = await PayslipService.getPayslipsByEmployee(currentUserEmployeeId);
        payslipsData = payslipsData.filter((payslip) => payslip.period === period);
        payslipsData = payslipsData.filter((p) =>
          ['approved', 'paid'].includes(String(p.status || '').toLowerCase())
        );
      } else if (isEmployee && !currentUserEmployeeId) {
        payslipsData = [];
        toast({
          title: 'Profile not linked',
          description: 'Your user account is not linked to an employee record. Contact HR.',
          variant: 'destructive',
        });
      } else {
        // For admin/HR/finance, get all payslips for the period
        payslipsData = await PayslipService.getPayslipsByPeriod(period, user?.cid ?? null);
      }
      
      if (payslipsData && Array.isArray(payslipsData)) {
        setPayslips(payslipsData);
        toast({
          title: "Success",
          description: `Loaded ${payslipsData.length} payslips for ${period}.`,
        });
      } else {
        setPayslips([]);
        toast({
          title: "No Data",
          description: `No payslips found for ${period}.`,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error loading payslips for period:', error);
      setPayslips([]);
      toast({
        title: "Error",
        description: `Failed to load payslips for ${period}.`,
        variant: "destructive"
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleViewPayslip = (payslip: Payslip) => {
    setSelectedPayslip(payslip);
  };

  const handleDownloadPayslip = async () => {
    if (!selectedPayslip) return;
    
    try {
      // Direct client-side PDF generation
      const element = document.getElementById('payslip-document');
      if (element) {
        const html2pdf = (await import('html2pdf.js')).default as any;
        
        // Clone the element to avoid modifying the original
        const clonedElement = element.cloneNode(true) as HTMLElement;
        
        // Add print-specific styles
        const style = document.createElement('style');
        style.textContent = PAYSLIP_EXPORT_STYLES;
        
        // Create a temporary container
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.top = '0';
        tempContainer.appendChild(style);
        tempContainer.appendChild(clonedElement);
        document.body.appendChild(tempContainer);
        
        const manNo = getEmployeeManNumber(selectedPayslip);
        const opt = {
          margin: [0.25, 0.25, 0.25, 0.25],
          filename: `payslip-${manNo}-${selectedPayslip.period || 'unknown'}.pdf`,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { 
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff'
          },
          jsPDF: { 
            unit: 'in', 
            format: 'a4', 
            orientation: 'portrait',
            compress: true
          },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        };
        
        await html2pdf().set(opt).from(clonedElement).save();
        
        // Clean up
        document.body.removeChild(tempContainer);
        
        toast({
          title: "Success",
          description: "Payslip downloaded successfully.",
        });
      } else {
        throw new Error('Payslip document element not found');
      }
    } catch (error) {
      console.error('PDF generation failed:', error);
      
      // Fallback: Use browser's print to PDF
      try {
        const element = document.getElementById('payslip-document');
        if (element) {
          const printWindow = window.open('', '_blank');
          if (printWindow) {
            const clonedElement = element.cloneNode(true) as HTMLElement;
            
            printWindow.document.write(`
              <!DOCTYPE html>
              <html>
                <head>
                  <title>Payslip - ${selectedPayslip.employeeIdNumber || selectedPayslip.employeeId}</title>
                  <meta charset="utf-8">
                  <style>
                    * { box-sizing: border-box; }
                    body { 
                      margin: 0; 
                      padding: 20px; 
                      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                      background: white;
                      color: #111827;
                    }
                    .bg-white { background: white !important; }
                    .text-gray-900 { color: #111827 !important; }
                    .text-gray-600 { color: #4B5563 !important; }
                    .text-gray-500 { color: #6B7280 !important; }
                    .bg-blue-600 { background: #2563EB !important; }
                    .bg-green-50 { background: #F0FDF4 !important; }
                    .bg-red-50 { background: #FEF2F2 !important; }
                    .bg-blue-50 { background: #EFF6FF !important; }
                    .bg-gray-50 { background: #F9FAFB !important; }
                    .text-green-800 { color: #166534 !important; }
                    .text-red-800 { color: #991B1B !important; }
                    .text-blue-900 { color: #1E3A8A !important; }
                    .text-blue-700 { color: #1D4ED8 !important; }
                    .text-red-600 { color: #DC2626 !important; }
                    .text-green-600 { color: #16A34A !important; }
                    .border-gray-200 { border-color: #E5E7EB !important; }
                    .border-gray-400 { border-color: #9CA3AF !important; }
                    .rounded-lg { border-radius: 0.5rem !important; }
                    .rounded { border-radius: 0.25rem !important; }
                  </style>
                </head>
                <body>
                  ${clonedElement.outerHTML}
                  <script>
                    window.onload = function() {
                      setTimeout(function() {
                        window.print();
                      }, 500);
                    };
                  </script>
                </body>
              </html>
            `);
            printWindow.document.close();
            
            toast({
              title: "Print to PDF",
              description: "Use Ctrl+P (Cmd+P on Mac) and select 'Save as PDF' to download.",
            });
          }
        }
      } catch (finalError) {
        console.error('All download methods failed:', finalError);
      toast({
        title: "Error",
          description: "Failed to download payslip. Please try the print option instead.",
        variant: "destructive"
      });
      }
    }
  };

  const handlePrintPayslip = async () => {
    if (!selectedPayslip) return;
    
    try {
      // Use the same html2pdf approach as download for exact consistency
      const element = document.getElementById('payslip-document');
      if (element) {
        const html2pdf = (await import('html2pdf.js')).default as any;
        
        // Clone the element to avoid modifying the original
        const clonedElement = element.cloneNode(true) as HTMLElement;
        
        // Add print-specific styles (same as download)
        const style = document.createElement('style');
        style.textContent = PAYSLIP_EXPORT_STYLES;
        
        // Create a temporary container
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.top = '0';
        tempContainer.appendChild(style);
        tempContainer.appendChild(clonedElement);
        document.body.appendChild(tempContainer);
        
        const manNo = getEmployeeManNumber(selectedPayslip);
        const opt = {
          margin: [0.25, 0.25, 0.25, 0.25],
          filename: `payslip-${manNo}-${selectedPayslip.period || 'unknown'}.pdf`,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { 
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff'
          },
          jsPDF: { 
            unit: 'in', 
            format: 'a4', 
            orientation: 'portrait',
            compress: true
          },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        };
        
        // Generate PDF and open in new window for printing
        const pdfBlob = await html2pdf().set(opt).from(clonedElement).outputPdf('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        
        // Open PDF in new window for printing
        const printWindow = window.open(pdfUrl, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
            // Clean up the URL after printing
            setTimeout(() => {
              URL.revokeObjectURL(pdfUrl);
            }, 1000);
          };
          
          toast({
            title: "Success",
            description: "Payslip opened for printing.",
          });
        }
        
        // Clean up
        document.body.removeChild(tempContainer);
      } else {
        throw new Error('Payslip document element not found');
      }
    } catch (error) {
      console.error('Print failed:', error);
      
      // Fallback: Use browser's print to PDF
      try {
        const element = document.getElementById('payslip-document');
        if (element) {
          const printWindow = window.open('', '_blank');
          if (printWindow) {
            const clonedElement = element.cloneNode(true) as HTMLElement;
            
            printWindow.document.write(`
              <!DOCTYPE html>
              <html>
                <head>
                  <title>Payslip - ${selectedPayslip.employeeIdNumber || selectedPayslip.employeeId}</title>
                  <meta charset="utf-8">
                  <style>
                    * { box-sizing: border-box; }
                    body { 
                      margin: 0; 
                      padding: 20px; 
                      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                      background: white;
                      color: #111827;
                    }
                    .bg-white { background: white !important; }
                    .text-gray-900 { color: #111827 !important; }
                    .text-gray-600 { color: #4B5563 !important; }
                    .text-gray-500 { color: #6B7280 !important; }
                    .bg-blue-600 { background: #2563EB !important; }
                    .bg-green-50 { background: #F0FDF4 !important; }
                    .bg-red-50 { background: #FEF2F2 !important; }
                    .bg-blue-50 { background: #EFF6FF !important; }
                    .bg-gray-50 { background: #F9FAFB !important; }
                    .text-green-800 { color: #166534 !important; }
                    .text-red-800 { color: #991B1B !important; }
                    .text-blue-900 { color: #1E3A8A !important; }
                    .text-blue-700 { color: #1D4ED8 !important; }
                    .text-red-600 { color: #DC2626 !important; }
                    .text-green-600 { color: #16A34A !important; }
                    .border-gray-200 { border-color: #E5E7EB !important; }
                    .border-gray-400 { border-color: #9CA3AF !important; }
                    .rounded-lg { border-radius: 0.5rem !important; }
                    .rounded { border-radius: 0.25rem !important; }
                  </style>
                </head>
                <body>
                  ${clonedElement.outerHTML}
                  <script>
                    window.onload = function() {
                      setTimeout(function() {
                        window.print();
                      }, 500);
                    };
                  </script>
                </body>
              </html>
            `);
            printWindow.document.close();
            
            toast({
              title: "Print to PDF",
              description: "Use Ctrl+P (Cmd+P on Mac) and select 'Save as PDF' to download.",
            });
          }
        }
      } catch (finalError) {
        console.error('All print methods failed:', finalError);
        toast({
          title: "Error",
          description: "Failed to print payslip. Please try again.",
          variant: "destructive"
        });
      }
    }
  };

  const handleEmailPayslip = () => {
    if (!selectedPayslip) return;

 
    // const status = (selectedPayslip.status || '').trim().toLowerCase();
    // if (status !== 'approved') {
    //   toast({
    //     title: "Email not allowed",
    //     description: "You can only email payslips that are approved.",
    //     variant: "destructive"
    //   });
    //   return;
    // }
    setEmailDialogOpen(true);
  };

  /** IDs for ZIP export: current filtered period list (status filter already applied). */
  const getBulkDownloadIds = () => {
    const periodPayslips = filteredPayslips.filter(
      (p) => (p.period || p.payPeriod) === currentPeriod
    );
    return periodPayslips.map((p) => p.id).filter(Boolean);
  };

  const handleBulkDownloadZip = async () => {
    const ids = getBulkDownloadIds();
    if (ids.length === 0) {
      toast({
        title: 'No payslips',
        description: `No payslips match the current filters for ${currentPeriod}.`,
        variant: 'destructive',
      });
      return;
    }

    try {
      setBulkDownloading(true);
      const result = await PayslipService.bulkDownloadPayslips(ids);
      toast({
        title: 'ZIP downloaded',
        description: `Saved ${result.count} payslip${result.count === 1 ? '' : 's'} as ${result.filename}.`,
      });
    } catch (error) {
      toast({
        title: 'Download failed',
        description: getApiErrorMessage(error, 'Failed to download payslip ZIP.'),
        variant: 'destructive',
      });
    } finally {
      setBulkDownloading(false);
    }
  };

  const getEmployeeName = (payslip: Payslip) => {
    if (payslip.employeeName) {
      return payslip.employeeName;
    }
    const employee = employees.find(emp => emp.id === payslip.employeeId);
    return employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown Employee';
  };

  const getEmployeeDepartment = (payslip: Payslip) => {
    const employee = employees.find(emp => emp.id === payslip.employeeId);
    if (!employee) return payslip.department || '';
    const dept = departments.find(d => d.id === employee.department_id);
    return (dept && (dept as any).name) || payslip.department || '';
  };

  const getEmployeeManNumber = (payslip: Payslip) => {
    const employee = employees.find(emp => emp.id === payslip.employeeId);
    return employee?.manNumber || payslip.manNumber || 'N/A';
  };

  const getEmployeePosition = (payslip: Payslip) => {
    const employee = employees.find(emp => emp.id === payslip.employeeId);
    if (!employee) return payslip.position || '';
    const pos = positions.find(p => p.id === employee.position_id);
    // Backend may use 'title' or 'name' for position label
    return ((pos as any)?.title || pos?.name) || payslip.position || '';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'approved': return 'bg-blue-100 text-blue-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusCounts = () => {
    const counts = { all: 0, draft: 0, approved: 0, paid: 0 };
    counts.all = payslips.length;
    payslips.forEach(p => {
      const status = (p.status || '').trim().toLowerCase();
      if (status === 'draft') counts.draft++;
      else if (status === 'approved') counts.approved++;
      else if (status === 'paid') counts.paid++;
    });
    return counts;
  };
  
  const statusCounts = getStatusCounts();

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 w-full max-w-full overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold">Payslips</h1>
            <p className="text-sm sm:text-base text-gray-600">
              {isEmployee ? 'View your payslips' : 'Manage and view employee payslips'}
            </p>
            </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            {!isEmployee && (
              <Button onClick={() => navigate('/payroll')} variant="outline" className="flex-1 sm:flex-none">
                <Plus className="h-4 w-4 mr-2" />
                Generate Payslips
              </Button>
            )}
            {!isEmployee && (
              <Button
                onClick={handleBulkDownloadZip}
                variant="outline"
                disabled={bulkDownloading || filteredPayslips.length === 0}
                className="flex-1 sm:flex-none"
              >
                <Archive className={`h-4 w-4 mr-2 ${bulkDownloading ? 'animate-pulse' : ''}`} />
                {bulkDownloading ? 'Preparing ZIP…' : `Download ZIP (${filteredPayslips.length})`}
              </Button>
            )}
            <Button
              onClick={() => loadPayslipsForPeriod(currentPeriod)}
              disabled={refreshing}
              className="flex-1 sm:flex-none"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Filter className="h-5 w-5 shrink-0" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`grid grid-cols-1 gap-3 sm:gap-4 ${isEmployee ? 'sm:grid-cols-2 md:grid-cols-3' : 'sm:grid-cols-2 md:grid-cols-4'}`}>
              {!isEmployee && (
                <div className="sm:col-span-2 md:col-span-1">
                  <label className="text-sm font-medium">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by name or ID..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="pl-10"
                />
              </div>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium">Year</label>
                <Select value={filters.year} onValueChange={(value) => handleFilterChange('year', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium">Month</label>
                <Select value={filters.month} onValueChange={(value) => handleFilterChange('month', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => {
                      const month = (i + 1).toString().padStart(2, '0');
                      const monthName = new Date(2024, i).toLocaleString('default', { month: 'long' });
                      return <SelectItem key={month} value={month}>{monthName}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium">Status</label>
                <Select value={filters.status} onValueChange={(value) => handleFilterChange('status', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All ({statusCounts.all})</SelectItem>
                    {!isEmployee && (
                      <SelectItem value="draft">Draft ({statusCounts.draft})</SelectItem>
                    )}
                    <SelectItem value="approved">Approved ({statusCounts.approved})</SelectItem>
                    <SelectItem value="paid">Paid ({statusCounts.paid})</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Payslips List */}
          <div className="lg:col-span-1 min-w-0">
            <Card className="h-auto max-h-[50vh] lg:max-h-none lg:h-[calc(100vh-300px)] overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <User className="h-5 w-5 shrink-0" />
                  <span className="truncate">
                    {isEmployee ? 'Your Payslips' : 'Payslips'} ({filteredPayslips.length})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-y-auto max-h-[calc(50vh-4rem)] lg:max-h-[calc(100vh-360px)]">
                {refreshing ? (
                  <div className="flex items-center justify-center h-64">
                    <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : filteredPayslips.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 px-4">
                    <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No payslips found</p>
                    <p className="text-sm">
                      {isEmployee ? 'No payslips available for this period' : 'Try adjusting your filters'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredPayslips.map((payslip) => (
                      <div
                        key={payslip.id}
                        className={`p-3 sm:p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedPayslip?.id === payslip.id ? 'bg-blue-50 border-r-4 border-blue-500' : ''
                        }`}
                        onClick={() => handleViewPayslip(payslip)}
                      >
                        <div className="flex justify-between items-start gap-2 mb-2">
                          <div className="min-w-0">
                            <p className="font-medium truncate">{getEmployeeName(payslip)}</p>
                            <p className="text-sm text-gray-600 truncate">{payslip.employeeIdNumber}</p>
                          </div>
                          <Badge className={`${getStatusColor(payslip.status)} shrink-0`}>
                            {payslip.status.charAt(0).toUpperCase() + payslip.status.slice(1)}
                          </Badge>
                        </div>
                        <div className="flex justify-between items-center gap-2 text-sm text-gray-600">
                          <span className="truncate">{payslip.period || payslip.payPeriod}</span>
                          <span className="font-medium shrink-0">{formatCurrency(payslip.netSalary)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Payslip Details */}
          <div className="lg:col-span-2 min-w-0">
            {selectedPayslip ? (
              <Card className="h-auto lg:h-[calc(100vh-300px)] overflow-hidden">
                <CardHeader className="space-y-3 pb-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                    <div className="min-w-0">
                      <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                        <DollarSign className="h-5 w-5 shrink-0" />
                        Payslip Details
                      </CardTitle>
                      <p className="text-sm text-gray-600 mt-1 truncate">
                        {getEmployeeName(selectedPayslip)} • {selectedPayslip.period || selectedPayslip.payPeriod}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                      <Button size="sm" variant="outline" onClick={handlePrintPayslip} className="flex-1 sm:flex-none">
                        <Printer className="h-4 w-4 mr-1" />
                        Print
                      </Button>
                      {!isEmployee && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleEmailPayslip}
                          className="flex-1 sm:flex-none"
                        >
                          <Mail className="h-4 w-4 mr-1" />
                          Email
                        </Button>
                      )}
                      <Button size="sm" onClick={handleDownloadPayslip} className="flex-1 sm:flex-none">
                        <Download className="h-4 w-4 mr-1" />
                        Download PDF
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="overflow-auto p-0 max-h-[70vh] lg:max-h-[calc(100vh-380px)]">
                  <PayslipDocument 
                    payslip={selectedPayslip}
                    employeeName={getEmployeeName(selectedPayslip)}
                    manNumber={getEmployeeManNumber(selectedPayslip)}
                    departmentName={getEmployeeDepartment(selectedPayslip)}
                    positionName={getEmployeePosition(selectedPayslip)}
                    council={council}
                  />
                </CardContent>
              </Card>
            ) : (
              <Card className="h-auto min-h-[200px] lg:h-[calc(100vh-300px)]">
                <CardContent className="flex items-center justify-center h-full py-12">
                  <div className="text-center text-gray-500 px-4">
                    <Eye className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">Select a payslip to view details</p>
                    <p className="text-sm">Choose a payslip from the list to see full details</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Email Dialog */}
      {selectedPayslip && (
        <EmailPayslipDialog
          key={selectedPayslip.id} // ensure dialog remounts per payslip to refresh props/state
          isOpen={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          payslipId={selectedPayslip.id}
          employeeName={getEmployeeName(selectedPayslip)}
          period={selectedPayslip.period}
          defaultEmail={
            selectedPayslip.employeeEmail ||
            employees.find(emp => emp.id === selectedPayslip.employeeId)?.email ||
            ''
          }
        />
      )}
    </DashboardLayout>
  );
};

export default Payslips;